import { ApiService } from './api';

/**
 * Campaign API Service
 * Handles all campaign-related API calls
 */
class CampaignApiService extends ApiService {
    constructor() {
        super();
        this.baseEndpoint = '/api/campaign';
    }

    // Campaign Management

    /**
     * Create a new campaign
     * @param {Object} campaignData - Campaign data
     * @returns {Promise<Object>} Created campaign
     */
    async createCampaign(campaignData) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns`, {
            method: 'POST',
            body: JSON.stringify(campaignData),
        });
    }

    /**
     * Get campaigns with pagination and filters
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Campaigns list with pagination info
     */
    async getCampaigns(params = {}) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns`, {
            params,
        });
    }

    /**
     * Get a campaign by ID
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Campaign details
     */
    async getCampaign(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}`);
    }

    /**
     * Update a campaign
     * @param {string} id - Campaign ID
     * @param {Object} data - Updated campaign data
     * @returns {Promise<Object>} Updated campaign
     */
    async updateCampaign(id, data) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    /**
     * Delete a campaign
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Deletion result
     */
    async deleteCampaign(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}`, {
            method: 'DELETE',
        });
    }

    /**
     * Duplicate a campaign
     * @param {string} id - Campaign ID to duplicate
     * @returns {Promise<Object>} New campaign
     */
    async duplicateCampaign(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/duplicate`, {
            method: 'POST',
        });
    }

    // Campaign Execution

    /**
     * Start a campaign
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Start result
     */
    async startCampaign(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/start`, {
            method: 'POST',
        });
    }

    /**
     * Schedule a campaign
     * @param {string} id - Campaign ID
     * @param {string} scheduleTime - ISO datetime string
     * @returns {Promise<Object>} Schedule result
     */
    async scheduleCampaign(id, scheduleTime) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/schedule`, {
            method: 'POST',
            body: JSON.stringify({ scheduleTime }),
        });
    }

    /**
     * Cancel a scheduled campaign
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Cancellation result
     */
    async cancelScheduledCampaign(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/cancel-schedule`, {
            method: 'POST',
        });
    }

    /**
     * Pause a running campaign
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Pause result
     */
    async pauseCampaign(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/pause`, {
            method: 'POST',
        });
    }

    /**
     * Resume a paused campaign
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Resume result
     */
    async resumeCampaign(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/resume`, {
            method: 'POST',
        });
    }

    /**
     * Cancel a campaign
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Cancellation result
     */
    async cancelCampaign(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/cancel`, {
            method: 'POST',
        });
    }

    /**
     * Get campaign status
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Campaign status
     */
    async getCampaignStatus(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/status`);
    }

    // Contact Management

    /**
     * Add contacts to a campaign
     * @param {string} id - Campaign ID
     * @param {Array<Object>} contacts - Contacts data
     * @returns {Promise<Object>} Add result
     */
    async addCampaignContacts(id, contacts) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/contacts`, {
            method: 'POST',
            body: JSON.stringify({ contacts }),
        });
    }

    /**
     * Upload contacts from a file
     * @param {string} id - Campaign ID
     * @param {File} file - File object
     * @param {Object} mapping - Column mapping
     * @returns {Promise<Object>} Upload result
     */
    async uploadCampaignContacts(id, file, mapping = null) {
        const formData = new FormData();
        formData.append('file', file);

        if (mapping) {
            formData.append('mapping', JSON.stringify(mapping));
        }

        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${this.baseUrl}${this.baseEndpoint}/campaigns/${id}/contacts/upload`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to upload contacts');
            }

            return response.json();
        } catch (error) {
            console.error('Contact upload error:', error);
            throw error;
        }
    }

    /**
     * Get campaign contacts
     * @param {string} id - Campaign ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Contacts with pagination
     */
    async getCampaignContacts(id, params = {}) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/contacts`, {
            params,
        });
    }

    /**
     * Delete a contact
     * @param {string} id - Campaign ID
     * @param {string} contactId - Contact ID
     * @returns {Promise<Object>} Deletion result
     */
    async deleteCampaignContact(id, contactId) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/contacts/${contactId}`, {
            method: 'DELETE',
        });
    }

    /**
     * Delete all contacts
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Deletion result
     */
    async deleteAllCampaignContacts(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/contacts`, {
            method: 'DELETE',
        });
    }

    // Reporting

    /**
     * Get campaign report
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Campaign report
     */
    async getCampaignReport(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/report`);
    }

    /**
     * Get delivery summary
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Delivery summary
     */
    async getDeliverySummary(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/delivery-summary`);
    }

    /**
     * Get batch analysis
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Batch analysis
     */
    async getBatchAnalysis(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/batch-analysis`);
    }

    /**
     * Get failure analysis
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Failure analysis
     */
    async getFailureAnalysis(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/failure-analysis`);
    }

    /**
     * Regenerate report
     * @param {string} id - Campaign ID
     * @returns {Promise<Object>} Regenerated report
     */
    async regenerateReport(id) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/${id}/regenerate-report`, {
            method: 'POST',
        });
    }

    /**
     * Get all reports
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Reports with pagination
     */
    async getAllReports(params = {}) {
        return this.fetchWithAuth(`${this.baseEndpoint}/campaigns/reports`, {
            params,
        });
    }

    // Templates

    /**
     * Get templates
     * @returns {Promise<Object>} Available templates
     */
    async getTemplates() {
        return this.fetchWithAuth(`${this.baseEndpoint}/templates`);
    }

    /**
     * Get template details
     * @param {string} name - Template name
     * @returns {Promise<Object>} Template details
     */
    async getTemplateDetails(name) {
        return this.fetchWithAuth(`${this.baseEndpoint}/templates/${name}`);
    }

    /**
     * Test template rendering
     * @param {Object} data - Template data
     * @returns {Promise<Object>} Processed template
     */
    async testTemplate(data) {
        return this.fetchWithAuth(`${this.baseEndpoint}/templates/test`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }
}

export default new CampaignApiService(); 